import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { adminApiClient } from '../lib/api';
import { Database, Eye, Merge, RefreshCw } from 'lucide-react';

const LegacyDataPage: React.FC = () => {
  const [jobs, setJobs] = useState<any[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string>('');
  const [job, setJob] = useState<any | null>(null);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [temples, setTemples] = useState<any[]>([]);
  const [templesLoadError, setTemplesLoadError] = useState('');
  const [mergeTempleId, setMergeTempleId] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [donorIdFilter, setDonorIdFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const limit = 50;

  const selectedJob = useMemo(() => jobs.find(j => j._id === selectedJobId) || null, [jobs, selectedJobId]);

  const fetchJobs = async () => {
    try {
      const res = await adminApiClient.getLegacyJobs();
      if (res.success) setJobs(res.data?.jobs || []);
    } catch {
      // ignore
    }
  };

  const fetchRecords = async (jobId: string, p: number, opts?: { q?: string; status?: string; donor_id?: string; startDate?: string; endDate?: string }) => {
    setLoading(true);
    try {
      const res = await adminApiClient.getLegacyJobRecords(jobId, {
        page: p,
        limit,
        includeMapped: false,
        ...(opts?.q ? { q: opts.q } : {}),
        ...(opts?.status ? { status: opts.status } : {}),
        ...(opts?.donor_id ? { donor_id: opts.donor_id } : {}),
        ...(opts?.startDate ? { startDate: opts.startDate } : {}),
        ...(opts?.endDate ? { endDate: opts.endDate } : {}),
      });
      if (res.success) {
        setJob(res.data?.job || null);
        setItems(res.data?.items || []);
        setTotal(res.data?.total || 0);
      } else {
        toast.error(res.message || 'Failed to load records');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    (async () => {
      try {
        const res = await adminApiClient.getTemples({ page: 1, limit: 200 });
        if (res.success) {
          setTemples(res.data?.temples || []);
          setTemplesLoadError('');
        } else {
          setTemplesLoadError(res.message || 'Failed to load temples');
        }
      } catch (e: any) {
        setTemplesLoadError(e?.response?.data?.message || e?.message || 'Failed to load temples');
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedJobId) return;
    setPage(1);
    fetchRecords(selectedJobId, 1, { q, status: statusFilter, donor_id: donorIdFilter, startDate, endDate });
    setMergeTempleId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJobId]);

  const canMerge = selectedJob?.datasetType === 'sales';
  const isContacts = selectedJob?.datasetType === 'contacts';
  const isDonors = selectedJob?.datasetType === 'donors';
  const isSales = selectedJob?.datasetType === 'sales';

  const handleApplyFilters = async () => {
    if (!selectedJobId) return;
    setPage(1);
    await fetchRecords(selectedJobId, 1, { q, status: statusFilter, donor_id: donorIdFilter, startDate, endDate });
  };

  const handleClearFilters = async () => {
    setQ('');
    setStatusFilter('');
    setDonorIdFilter('');
    setStartDate('');
    setEndDate('');
    if (!selectedJobId) return;
    setPage(1);
    await fetchRecords(selectedJobId, 1, {});
  };

  const handleMerge = async () => {
    if (!selectedJobId) return;
    if (!canMerge) {
      toast.error('Merge is only available for sales dataset');
      return;
    }
    if (!mergeTempleId) {
      toast.error('Please select a temple to merge sales into live donations');
      return;
    }
    try {
      setMerging(true);
      const res = await adminApiClient.mergeLegacySalesJob(selectedJobId, { templeId: mergeTempleId });
      if (res.success) {
        toast.success(`Merged: ${res.data?.merged || 0}, Errors: ${res.data?.errors || 0}`);
        await fetchJobs();
        await fetchRecords(selectedJobId, page, { q, status: statusFilter, donor_id: donorIdFilter, startDate, endDate });
      } else {
        toast.error(res.message || 'Merge failed');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  const handlePageChange = async (nextPage: number) => {
    if (!selectedJobId) return;
    setPage(nextPage);
    await fetchRecords(selectedJobId, nextPage, { q, status: statusFilter, donor_id: donorIdFilter, startDate, endDate });
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="p-6">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Database className="w-6 h-6 text-[var(--color-primary)]" />
            Legacy Data
          </h1>
          <p className="text-sm text-gray-600 mt-1">View imported old records (stored separately) and merge matched sales into live donations.</p>
        </div>
        <button
          onClick={fetchJobs}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm font-medium"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Import Job</label>
            <select
              value={selectedJobId}
              onChange={(e) => setSelectedJobId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_30%,white)] focus:border-[var(--color-primary)] outline-none"
            >
              <option value="">Choose a job</option>
              {jobs.map((j) => (
                <option key={j._id} value={j._id}>
                  {j.originalFileName} — {j.datasetType} — {j.status}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() => selectedJobId && fetchRecords(selectedJobId, page)}
              disabled={!selectedJobId || loading}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm font-medium disabled:opacity-60"
            >
              <Eye className="w-4 h-4" />
              {loading ? 'Loading…' : 'View'}
            </button>
            <button
              onClick={handleMerge}
              disabled={!selectedJobId || !canMerge || merging}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white font-semibold bg-[linear-gradient(90deg,var(--color-primary),var(--color-tertiary))] hover:opacity-90 disabled:opacity-60"
              title={!canMerge ? 'Only sales dataset supports merge to live donations' : undefined}
            >
              <Merge className="w-4 h-4" />
              {merging ? 'Merging…' : 'Merge to Live'}
            </button>
          </div>
        </div>

        {!selectedJobId ? (
          <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
            Select a <strong>sales</strong> import job to choose temple and merge records.
          </div>
        ) : null}

        {selectedJobId && !canMerge ? (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            Temple selection appears only for <strong>sales</strong> jobs. Current job type: <strong>{selectedJob?.datasetType}</strong>.
          </div>
        ) : null}

        {canMerge ? (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Temple to merge into (required)</label>
              <select
                value={mergeTempleId}
                onChange={(e) => setMergeTempleId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_30%,white)] focus:border-[var(--color-primary)] outline-none"
              >
                <option value="">Select temple</option>
                {temples.map((t) => (
                  <option key={t._id} value={t._id}>{t.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">This will create live donations under the selected temple.</p>
              {templesLoadError ? (
                <p className="text-xs text-red-600 mt-1">{templesLoadError}</p>
              ) : null}
            </div>
          </div>
        ) : null}

        {job ? (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-6 gap-3">
            {[
              ['Rows', job.totals?.rows ?? 0],
              ['Inserted', job.totals?.inserted ?? 0],
              ['Matched', job.totals?.matchedUsers ?? 0],
              ['Merge Ready', job.totals?.mergeReady ?? 0],
              ['Merged', job.totals?.merged ?? 0],
              ['Errors', job.totals?.errors ?? 0],
            ].map(([k, v]) => (
              <div key={String(k)} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">{k}</div>
                <div className="text-lg font-bold text-gray-900 tabular-nums">{v as any}</div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="mt-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Records</h2>
            <p className="text-sm text-gray-600 mt-1">Showing latest {limit} records (page {page}/{totalPages}).</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={!selectedJobId || page <= 1 || loading}
              onClick={() => handlePageChange(page - 1)}
              className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm font-medium disabled:opacity-60"
            >
              Prev
            </button>
            <button
              disabled={!selectedJobId || page >= totalPages || loading}
              onClick={() => handlePageChange(page + 1)}
              className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm font-medium disabled:opacity-60"
            >
              Next
            </button>
          </div>
        </div>

        <div className="px-5 py-4 border-b border-gray-100 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-5">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Global search</label>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, email, phone, city/state, product/service..."
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
              >
                <option value="">All</option>
                <option value="imported">imported</option>
                <option value="matched">matched</option>
                <option value="merge_ready">merge_ready</option>
                <option value="merged">merged</option>
                <option value="error">error</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Donor ID</label>
              <input
                value={donorIdFilter}
                onChange={(e) => setDonorIdFilter(e.target.value)}
                placeholder="e.g. 12"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            </div>
            {isSales ? (
              <>
                <div className="md:col-span-1">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">From</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">To</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </div>
              </>
            ) : null}
            <div className="md:col-span-2 flex gap-2">
              <button
                disabled={!selectedJobId || loading}
                onClick={handleApplyFilters}
                className="flex-1 px-3 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-700 text-sm font-semibold disabled:opacity-60"
              >
                Apply
              </button>
              <button
                disabled={!selectedJobId || loading}
                onClick={handleClearFilters}
                className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm font-medium disabled:opacity-60"
              >
                Clear
              </button>
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Tip: Use Donor ID to instantly view all linked donor/contact/sales rows that share the same `donor_id`.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                {canMerge ? (
                  <>
                    <th className="text-left px-5 py-3 font-semibold">Donor ID</th>
                    <th className="text-left px-5 py-3 font-semibold">Sale ID</th>
                    <th className="text-left px-5 py-3 font-semibold">Name</th>
                    <th className="text-left px-5 py-3 font-semibold">Date</th>
                    <th className="text-left px-5 py-3 font-semibold">Transaction Type</th>
                    <th className="text-left px-5 py-3 font-semibold">Num</th>
                    <th className="text-left px-5 py-3 font-semibold">Product/Service</th>
                    <th className="text-left px-5 py-3 font-semibold">Memo/Description</th>
                    <th className="text-right px-5 py-3 font-semibold">Qty</th>
                    <th className="text-right px-5 py-3 font-semibold">Sales Price</th>
                    <th className="text-right px-5 py-3 font-semibold">Amount</th>
                    <th className="text-right px-5 py-3 font-semibold">Balance</th>
                    <th className="text-left px-5 py-3 font-semibold">Status</th>
                  </>
                ) : isDonors ? (
                  <>
                    <th className="text-left px-5 py-3 font-semibold">Donor ID</th>
                    <th className="text-left px-5 py-3 font-semibold">Name</th>
                    <th className="text-left px-5 py-3 font-semibold">Company</th>
                    <th className="text-left px-5 py-3 font-semibold">Street</th>
                    <th className="text-left px-5 py-3 font-semibold">City</th>
                    <th className="text-left px-5 py-3 font-semibold">State</th>
                    <th className="text-left px-5 py-3 font-semibold">Country</th>
                    <th className="text-left px-5 py-3 font-semibold">Zip</th>
                    <th className="text-left px-5 py-3 font-semibold">Phone</th>
                    <th className="text-left px-5 py-3 font-semibold">Email</th>
                    <th className="text-left px-5 py-3 font-semibold">Donor type</th>
                    <th className="text-left px-5 py-3 font-semibold">Gothra</th>
                    <th className="text-left px-5 py-3 font-semibold">Nakshatram</th>
                    <th className="text-left px-5 py-3 font-semibold">Attachments</th>
                    <th className="text-right px-5 py-3 font-semibold">Open balance</th>
                    <th className="text-left px-5 py-3 font-semibold">Status</th>
                  </>
                ) : isContacts ? (
                  <>
                    <th className="text-left px-5 py-3 font-semibold">Contact ID</th>
                    <th className="text-left px-5 py-3 font-semibold">Donor ID</th>
                    <th className="text-left px-5 py-3 font-semibold">Name</th>
                    <th className="text-left px-5 py-3 font-semibold">Phone</th>
                    <th className="text-left px-5 py-3 font-semibold">Email</th>
                    <th className="text-left px-5 py-3 font-semibold">Billing address</th>
                    <th className="text-left px-5 py-3 font-semibold">Shipping address</th>
                    <th className="text-left px-5 py-3 font-semibold">Status</th>
                  </>
                ) : (
                  <>
                    <th className="text-left px-5 py-3 font-semibold">Name</th>
                    <th className="text-left px-5 py-3 font-semibold">Email</th>
                    <th className="text-left px-5 py-3 font-semibold">Phone</th>
                    <th className="text-left px-5 py-3 font-semibold">Address</th>
                    <th className="text-left px-5 py-3 font-semibold">Status</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {!selectedJobId ? (
                <tr><td className="px-5 py-6 text-gray-500" colSpan={20}>Select a job to view records.</td></tr>
              ) : loading ? (
                <tr><td className="px-5 py-6 text-gray-500" colSpan={20}>Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td className="px-5 py-6 text-gray-500" colSpan={20}>No records.</td></tr>
              ) : items.map((r, idx) => (
                <tr key={r._id} className="hover:bg-gray-50">
                  {canMerge ? (
                    <>
                      <td className="px-5 py-3 text-gray-700 tabular-nums">{typeof r.donorId === 'number' ? r.donorId : '-'}</td>
                      <td className="px-5 py-3 text-gray-700 tabular-nums">{typeof r.saleId === 'number' ? r.saleId : '-'}</td>
                      <td className="px-5 py-3 text-gray-900">{r.donorName || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.donationDate ? new Date(r.donationDate).toLocaleDateString() : '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.transactionType || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.num || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.productOrService || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.memoOrDescription || '-'}</td>
                      <td className="px-5 py-3 text-right tabular-nums">{typeof r.qty === 'number' ? r.qty : '-'}</td>
                      <td className="px-5 py-3 text-right tabular-nums">{typeof r.salesPrice === 'number' ? `$${r.salesPrice.toFixed(2)}` : '-'}</td>
                      <td className="px-5 py-3 text-right tabular-nums">{typeof r.amount === 'number' ? `$${r.amount.toFixed(2)}` : '-'}</td>
                      <td className="px-5 py-3 text-right tabular-nums">{typeof r.balance === 'number' ? `$${r.balance.toFixed(2)}` : '-'}</td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-[color-mix(in_srgb,var(--color-primary)_10%,white)] text-[var(--color-primary)]">
                          {r.status}
                        </span>
                      </td>
                    </>
                  ) : isDonors ? (
                    <>
                      <td className="px-5 py-3 text-gray-700 tabular-nums">{typeof r.donorId === 'number' ? r.donorId : '-'}</td>
                      <td className="px-5 py-3 text-gray-900">{r.name || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.companyName || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.streetAddress || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.city || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.state || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.country || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.zip || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{(r.phones && r.phones.length ? r.phones.join(', ') : r.phone) || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{(r.emails && r.emails.length ? r.emails.join(', ') : r.email) || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.donorType || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.gothraDetails || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.nakshatram || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.attachments || '-'}</td>
                      <td className="px-5 py-3 text-right tabular-nums">{typeof r.openBalance === 'number' ? `$${r.openBalance.toFixed(2)}` : '-'}</td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-[color-mix(in_srgb,var(--color-primary)_10%,white)] text-[var(--color-primary)]">
                          {r.status}
                        </span>
                      </td>
                    </>
                  ) : isContacts ? (
                    <>
                      <td className="px-5 py-3 text-gray-700 tabular-nums">{typeof r.contactId === 'number' ? r.contactId : '-'}</td>
                      <td className="px-5 py-3 text-gray-700 tabular-nums">{typeof r.donorId === 'number' ? r.donorId : '-'}</td>
                      <td className="px-5 py-3 text-gray-900">{r.name || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{(r.phones && r.phones.length ? r.phones.join(', ') : r.phone) || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{(r.emails && r.emails.length ? r.emails.join(', ') : r.email) || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.billAddress || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.shipAddress || '-'}</td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-[color-mix(in_srgb,var(--color-primary)_10%,white)] text-[var(--color-primary)]">
                          {r.status}
                        </span>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-5 py-3 text-gray-900">{r.name || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{(r.emails && r.emails.length ? r.emails.join(', ') : r.email) || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{(r.phones && r.phones.length ? r.phones.join(', ') : r.phone) || '-'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.address || '-'}</td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-[color-mix(in_srgb,var(--color-primary)_10%,white)] text-[var(--color-primary)]">
                          {r.status}
                        </span>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LegacyDataPage;

